import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SamplePreview = ({ ebook }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < ebook?.samplePages?.length - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <div className="bg-card rounded-lg shadow-md overflow-hidden">
      <div className="bg-primary/5 px-4 md:px-6 py-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
            Sample Preview
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleFullscreen}
            iconName={isFullscreen ? 'Minimize2' : 'Maximize2'}
            iconPosition="left"
          >
            {isFullscreen ? 'Exit' : 'Fullscreen'}
          </Button>
        </div>
      </div>
      <div className={`relative ${isFullscreen ? 'fixed inset-0 z-300 bg-background' : ''}`}>
        {isFullscreen && (
          <div className="absolute top-4 right-4 z-10">
            <Button
              variant="outline"
              size="icon"
              onClick={toggleFullscreen}
            >
              <Icon name="X" size={20} />
            </Button>
          </div>
        )}

        <div className={`${isFullscreen ? 'h-screen flex flex-col' : ''}`}>
          <div className={`bg-muted ${isFullscreen ? 'flex-1 overflow-auto' : 'aspect-[8.5/11]'} relative`}>
            <div className="absolute inset-0 flex items-center justify-center p-4 md:p-8">
              <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full p-6 md:p-12 space-y-6 overflow-auto max-h-full">
                <div className="space-y-4">
                  <h3 className="text-2xl md:text-3xl font-heading font-bold text-gray-900">
                    {ebook?.samplePages?.[currentPage]?.title}
                  </h3>
                  <div className="prose prose-sm md:prose-base max-w-none text-gray-800 leading-relaxed whitespace-pre-line">
                    {ebook?.samplePages?.[currentPage]?.content}
                  </div>
                </div>

                {ebook?.samplePages?.[currentPage]?.framework && (
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 md:p-6 rounded-r space-y-3">
                    <h4 className="text-lg font-heading font-semibold text-blue-900">
                      Framework: {ebook?.samplePages?.[currentPage]?.framework?.title}
                    </h4>
                    <ul className="space-y-2">
                      {ebook?.samplePages?.[currentPage]?.framework?.steps?.map((step, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-blue-800">
                          <span className="font-bold">{index + 1}.</span>
                          <span>{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={`bg-card border-t border-border px-4 md:px-6 py-4 ${isFullscreen ? 'flex-shrink-0' : ''}`}>
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                onClick={handlePrevPage}
                disabled={currentPage === 0}
                iconName="ChevronLeft"
                iconPosition="left"
              >
                Previous
              </Button>

              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground data-text">
                  Page {currentPage + 1} of {ebook?.samplePages?.length}
                </span>
                <div className="flex gap-1">
                  {ebook?.samplePages?.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentPage(index)}
                      className={`w-2 h-2 rounded-full transition-smooth ${
                        index === currentPage ? 'bg-primary' : 'bg-muted-foreground/30'
                      }`}
                      aria-label={`Go to page ${index + 1}`}
                    />
                  ))}
                </div>
              </div>

              <Button
                variant="outline"
                onClick={handleNextPage}
                disabled={currentPage === ebook?.samplePages?.length - 1}
                iconName="ChevronRight"
                iconPosition="right"
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="px-4 md:px-6 py-4 bg-primary/5 border-t border-border">
        <p className="text-sm text-center text-muted-foreground">
          This is a sample preview. Purchase to access all {ebook?.pageCount} pages.
        </p>
      </div>
    </div>
  );
};

export default SamplePreview;